<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue';
import { InformationCircleIcon } from '@hugeicons/core-free-icons';
import { useI18n } from 'vue-i18n';

import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import OptionSelect from 'dashboard/components-next/select/OptionSelect.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  teams: {
    type: Array,
    default: () => [],
  },
  dropdownMaxHeight: {
    type: String,
    default: 'max-h-[18rem]',
  },
  errorMessage: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const modelValue = defineModel({
  type: [Object, Array, null],
  default: null,
});

const ROUND_ROBIN = 'round_robin';
const LEAST_OPEN_CONTACTS = 'least_open_contacts';
const VALID_ASSIGNMENT_LOGIC = new Set([ROUND_ROBIN, LEAST_OPEN_CONTACTS]);
const DEFAULT_MAX_OPEN_CONTACTS = 20;
const DEFAULT_TIMEOUT_VALUE = 7;
const DEFAULT_TIMEOUT_UNIT = 'days';

const { t } = useI18n();
const instance = getCurrentInstance();

const activeHint = ref(null);

const createDefaultValue = () => ({
  team_id: null,
  assignment_logic: ROUND_ROBIN,
  only_online_users: false,
  limit_open_contacts_enabled: false,
  max_open_contacts: DEFAULT_MAX_OPEN_CONTACTS,
  add_timeout_branch: false,
  timeout_value: DEFAULT_TIMEOUT_VALUE,
  timeout_unit: DEFAULT_TIMEOUT_UNIT,
});

const normalizedValue = computed(() => {
  if (modelValue.value && !Array.isArray(modelValue.value)) {
    return {
      ...createDefaultValue(),
      ...modelValue.value,
      team_id: modelValue.value.team_id || null,
      assignment_logic: modelValue.value.assignment_logic || ROUND_ROBIN,
      only_online_users: Boolean(modelValue.value.only_online_users),
      limit_open_contacts_enabled: Boolean(
        modelValue.value.limit_open_contacts_enabled
      ),
      max_open_contacts:
        modelValue.value.max_open_contacts ?? DEFAULT_MAX_OPEN_CONTACTS,
      add_timeout_branch: Boolean(modelValue.value.add_timeout_branch),
      timeout_value: modelValue.value.timeout_value ?? DEFAULT_TIMEOUT_VALUE,
      timeout_unit: modelValue.value.timeout_unit || DEFAULT_TIMEOUT_UNIT,
    };
  }

  return createDefaultValue();
});

const teamOptions = computed(() =>
  props.teams.map(team => ({
    key: team.id,
    label: team.name,
  }))
);

const timeoutUnitOptions = computed(() => [
  {
    key: 'minutes',
    label: t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.TIMEOUT_UNITS.MINUTES'),
  },
  {
    key: 'hours',
    label: t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.TIMEOUT_UNITS.HOURS'),
  },
  {
    key: 'days',
    label: t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.TIMEOUT_UNITS.DAYS'),
  },
]);

const selectedTeamKey = computed(() => normalizedValue.value.team_id?.id || '');
const hasTeamError = computed(
  () => Boolean(props.errorMessage) && !normalizedValue.value.team_id
);
const assignmentLogicName = computed(
  () => `assignment-logic-${instance?.uid ?? 'team'}`
);

const assignmentLogicOptions = computed(() => [
  {
    value: ROUND_ROBIN,
    label: t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.ROUND_ROBIN'),
    hint: t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.ROUND_ROBIN_HINT'),
  },
  {
    value: LEAST_OPEN_CONTACTS,
    label: t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.LEAST_OPEN_CONTACTS'),
    hint: t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.LEAST_OPEN_CONTACTS_HINT'),
  },
]);

const advancedSettingHints = computed(() => ({
  only_online_users: t(
    'AUTOMATION.ACTION.TEAM_ASSIGNMENT.ONLY_ONLINE_USERS_HINT'
  ),
  limit_open_contacts: t(
    'AUTOMATION.ACTION.TEAM_ASSIGNMENT.LIMIT_OPEN_CONTACTS_HINT'
  ),
  add_timeout_branch: t(
    'AUTOMATION.ACTION.TEAM_ASSIGNMENT.ADD_TIMEOUT_BRANCH_HINT'
  ),
  timeout: t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.TIMEOUT_HINT'),
}));

const showTimeoutBranchToggle = computed(
  () =>
    normalizedValue.value.only_online_users ||
    normalizedValue.value.limit_open_contacts_enabled
);

const showTimeoutBranchForm = computed(
  () =>
    showTimeoutBranchToggle.value && normalizedValue.value.add_timeout_branch
);

const updateValue = patch => {
  modelValue.value = {
    ...normalizedValue.value,
    ...patch,
  };
};

watch(
  normalizedValue,
  value => {
    if (
      modelValue.value &&
      !Array.isArray(modelValue.value) &&
      VALID_ASSIGNMENT_LOGIC.has(modelValue.value.assignment_logic)
    ) {
      return;
    }

    updateValue({ assignment_logic: ROUND_ROBIN });
  },
  { immediate: true }
);

watch(showTimeoutBranchToggle, isVisible => {
  if (!isVisible && normalizedValue.value.add_timeout_branch) {
    updateValue({ add_timeout_branch: false });
  }
});

const showHint = key => {
  activeHint.value = key;
};

const hideHint = () => {
  activeHint.value = null;
};

const onTeamChange = teamId => {
  const selectedTeam = props.teams.find(team => team.id === teamId) || null;
  updateValue({ team_id: selectedTeam });
};

const renderHint = key => activeHint.value === key;
</script>

<template>
  <div class="flex h-full min-h-0 w-full flex-col gap-5">
    <div
      class="flex min-h-0 flex-1 min-w-0 flex-col gap-5"
      :class="disabled ? 'pointer-events-none' : ''"
    >
      <div class="flex flex-col gap-1">
        <OptionSelect
          :model-value="selectedTeamKey"
          :options="teamOptions"
          :dropdown-max-height="dropdownMaxHeight"
          :has-error="hasTeamError"
          :disabled="disabled"
          :aria-label="$t('AUTOMATION.FLOW.ADD_STEPS.LABELS.ASSIGN_TO')"
          :placeholder="
            hasTeamError
              ? $t(
                  'AUTOMATION.ACTION.TEAM_ASSIGNMENT.TEAM_REQUIRED_PLACEHOLDER'
                )
              : ''
          "
          class="w-full"
          @update:model-value="onTeamChange"
        />
        <p v-if="hasTeamError" class="m-0 text-sm leading-5 text-n-ruby-11">
          {{ $t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.TEAM_REQUIRED_ERROR') }}
        </p>
      </div>

      <div class="flex flex-col gap-2.5">
        <label class="text-sm font-medium leading-5 text-n-slate-11">
          {{ $t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.LABEL') }}
        </label>

        <div class="flex flex-col gap-3">
          <label
            v-for="option in assignmentLogicOptions"
            :key="option.value"
            class="flex cursor-pointer items-center gap-2"
          >
            <input
              type="radio"
              :name="assignmentLogicName"
              class="h-4 w-4 shrink-0 border-n-slate-6 text-n-brand focus:ring-n-brand focus:ring-offset-0"
              :disabled="disabled"
              :checked="normalizedValue.assignment_logic === option.value"
              @change="updateValue({ assignment_logic: option.value })"
            />
            <span class="flex items-center gap-1.5 leading-none">
              <span class="text-base font-normal leading-6 text-n-slate-12">
                {{ option.label }}
              </span>
              <span class="relative inline-flex shrink-0">
                <button
                  type="button"
                  title=""
                  class="inline-flex size-5 shrink-0 cursor-help items-center justify-center text-n-slate-9"
                  :aria-label="option.label"
                  @mouseenter="showHint(option.value)"
                  @mouseleave="hideHint"
                  @focus="showHint(option.value)"
                  @blur="hideHint"
                  @click.prevent.stop
                >
                  <HugeIcon
                    :icon="InformationCircleIcon"
                    :size="18"
                    class="shrink-0"
                  />
                </button>
                <span
                  v-if="renderHint(option.value)"
                  class="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-[22rem] -translate-x-1/2 rounded-xl bg-n-slate-2 px-4 py-3 text-sm font-medium leading-6 text-n-slate-12 shadow-xl outline outline-1 outline-n-weak"
                >
                  {{ option.hint }}
                </span>
              </span>
            </span>
          </label>
        </div>
      </div>

      <div
        class="mt-auto flex flex-col gap-4 border-t border-n-weak pt-4 dark:border-n-strong"
      >
        <h3 class="m-0 text-sm font-medium leading-5 text-n-slate-12">
          {{ $t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.ADVANCED_SETTINGS') }}
        </h3>

        <div class="flex items-center gap-3">
          <Switch
            :model-value="normalizedValue.only_online_users"
            size="md"
            class="focus:!ring-0 focus-visible:!ring-0 focus:!ring-offset-0"
            @update:model-value="
              value => updateValue({ only_online_users: value })
            "
          />
          <div class="flex items-center gap-1.5">
            <span class="text-sm font-normal leading-5 text-n-slate-12">
              {{ $t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.ONLY_ONLINE_USERS') }}
            </span>
            <span class="relative inline-flex shrink-0">
              <button
                type="button"
                title=""
                class="inline-flex size-5 shrink-0 cursor-help items-center justify-center text-n-slate-9"
                :aria-label="
                  $t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.ONLY_ONLINE_USERS')
                "
                @mouseenter="showHint('only_online_users')"
                @mouseleave="hideHint"
                @focus="showHint('only_online_users')"
                @blur="hideHint"
                @click.prevent.stop
              >
                <HugeIcon
                  :icon="InformationCircleIcon"
                  :size="18"
                  class="shrink-0"
                />
              </button>
              <span
                v-if="renderHint('only_online_users')"
                class="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-[22rem] -translate-x-1/2 rounded-xl bg-n-slate-2 px-4 py-3 text-sm font-medium leading-6 text-n-slate-12 shadow-xl outline outline-1 outline-n-weak"
              >
                {{ advancedSettingHints.only_online_users }}
              </span>
            </span>
          </div>
        </div>

        <div class="flex flex-col gap-2 pt-3">
          <div class="flex items-center gap-3">
            <Switch
              :model-value="normalizedValue.limit_open_contacts_enabled"
              size="md"
              class="focus:!ring-0 focus-visible:!ring-0 focus:!ring-offset-0"
              @update:model-value="
                value => updateValue({ limit_open_contacts_enabled: value })
              "
            />
            <div class="flex flex-col gap-1.5">
              <span class="text-sm font-normal leading-5 text-n-slate-12">
                {{
                  $t(
                    'AUTOMATION.ACTION.TEAM_ASSIGNMENT.ONLY_ASSIGN_WITH_FEWER_THAN'
                  )
                }}
              </span>
              <Input
                :model-value="normalizedValue.max_open_contacts"
                type="number"
                min="1"
                custom-input-class="!w-14 !outline !outline-1 !outline-n-slate-6 hover:!outline-n-slate-6 focus:!outline-n-brand"
                :disabled="
                  disabled || !normalizedValue.limit_open_contacts_enabled
                "
                @update:model-value="
                  value => updateValue({ max_open_contacts: value })
                "
              />
              <span class="flex items-center gap-1.5">
                <span class="text-sm font-normal leading-5 text-n-slate-12">
                  {{
                    $t(
                      'AUTOMATION.ACTION.TEAM_ASSIGNMENT.OPEN_CONTACTS_ASSIGNED'
                    )
                  }}
                </span>
                <span class="relative inline-flex shrink-0">
                  <button
                    type="button"
                    title=""
                    class="inline-flex size-5 shrink-0 cursor-help items-center justify-center text-n-slate-9"
                    :aria-label="
                      $t(
                        'AUTOMATION.ACTION.TEAM_ASSIGNMENT.ONLY_ASSIGN_WITH_FEWER_THAN'
                      )
                    "
                    @mouseenter="showHint('limit_open_contacts')"
                    @mouseleave="hideHint"
                    @focus="showHint('limit_open_contacts')"
                    @blur="hideHint"
                    @click.prevent.stop
                  >
                    <HugeIcon
                      :icon="InformationCircleIcon"
                      :size="18"
                      class="shrink-0"
                    />
                  </button>
                  <span
                    v-if="renderHint('limit_open_contacts')"
                    class="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-[22rem] -translate-x-1/2 rounded-xl bg-n-slate-2 px-4 py-3 text-sm font-medium leading-6 text-n-slate-12 shadow-xl outline outline-1 outline-n-weak"
                  >
                    {{ advancedSettingHints.limit_open_contacts }}
                  </span>
                </span>
              </span>
            </div>
          </div>
        </div>

        <div v-if="showTimeoutBranchToggle" class="flex flex-col gap-4 pt-3">
          <div class="flex items-center gap-3">
            <Switch
              :model-value="normalizedValue.add_timeout_branch"
              size="md"
              class="focus:!ring-0 focus-visible:!ring-0 focus:!ring-offset-0"
              @update:model-value="
                value => updateValue({ add_timeout_branch: value })
              "
            />
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-normal leading-5 text-n-slate-12">
                {{ $t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.ADD_TIMEOUT_BRANCH') }}
              </span>
              <span class="relative inline-flex shrink-0">
                <button
                  type="button"
                  title=""
                  class="inline-flex size-5 shrink-0 cursor-help items-center justify-center text-n-slate-9"
                  :aria-label="
                    $t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.ADD_TIMEOUT_BRANCH')
                  "
                  @mouseenter="showHint('add_timeout_branch')"
                  @mouseleave="hideHint"
                  @focus="showHint('add_timeout_branch')"
                  @blur="hideHint"
                  @click.prevent.stop
                >
                  <HugeIcon
                    :icon="InformationCircleIcon"
                    :size="18"
                    class="shrink-0"
                  />
                </button>
                <span
                  v-if="renderHint('add_timeout_branch')"
                  class="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-[22rem] -translate-x-1/2 rounded-xl bg-n-slate-2 px-4 py-3 text-sm font-medium leading-6 text-n-slate-12 shadow-xl outline outline-1 outline-n-weak"
                >
                  {{ advancedSettingHints.add_timeout_branch }}
                </span>
              </span>
            </div>
          </div>

          <div v-if="showTimeoutBranchForm" class="flex flex-col gap-2.5">
            <div class="flex items-center gap-1.5">
              <label class="text-sm font-medium leading-5 text-n-slate-11">
                {{ $t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.TIMEOUT') }}
              </label>
              <span class="relative inline-flex shrink-0">
                <button
                  type="button"
                  title=""
                  class="inline-flex size-5 shrink-0 cursor-help items-center justify-center text-n-slate-9"
                  :aria-label="$t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.TIMEOUT')"
                  @mouseenter="showHint('timeout')"
                  @mouseleave="hideHint"
                  @focus="showHint('timeout')"
                  @blur="hideHint"
                  @click.prevent.stop
                >
                  <HugeIcon
                    :icon="InformationCircleIcon"
                    :size="18"
                    class="shrink-0"
                  />
                </button>
                <span
                  v-if="renderHint('timeout')"
                  class="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 w-[22rem] -translate-x-1/2 rounded-xl bg-n-slate-2 px-4 py-3 text-sm font-medium leading-6 text-n-slate-12 shadow-xl outline outline-1 outline-n-weak"
                >
                  {{ advancedSettingHints.timeout }}
                </span>
              </span>
            </div>

            <div class="grid grid-cols-[minmax(0,1fr)_12rem] gap-2.5">
              <Input
                :model-value="normalizedValue.timeout_value"
                type="number"
                min="1"
                custom-input-class="!outline !outline-1 !outline-n-slate-6 hover:!outline-n-slate-6 focus:!outline-n-brand"
                :disabled="disabled"
                @update:model-value="
                  value => updateValue({ timeout_value: value })
                "
              />
              <OptionSelect
                :model-value="normalizedValue.timeout_unit"
                :options="timeoutUnitOptions"
                :disabled="disabled"
                :aria-label="$t('AUTOMATION.ACTION.TEAM_ASSIGNMENT.TIMEOUT')"
                dropdown-direction="up"
                dropdown-panel-class="!bg-n-slate-3 dark:!bg-n-slate-3 !border-n-slate-6 dark:!border-n-slate-6"
                @update:model-value="
                  value => updateValue({ timeout_unit: value })
                "
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
